import { WarningCircleIcon } from '@phosphor-icons/react';
import { useId, type ReactNode } from 'react';

import styles from './Field.module.css';

export interface FieldIds {
  /** Ставится на сам контрол (`id`), чтобы метка была связана через `htmlFor`. */
  fieldId: string;
  /** Готовое значение для `aria-describedby` контрола (или `undefined`). */
  describedBy: string | undefined;
  /** Присутствие ошибки: контрол обязан выставить `aria-invalid`. */
  invalid: boolean;
}

interface FieldProps {
  label: string;
  /** Метка скрывается только там, где контекст её дублирует (панель фильтров). */
  hideLabel?: boolean | undefined;
  hint?: string | undefined;
  /** Текст ошибки. Ошибка — текст плюс иконка, никогда только красная рамка. */
  error?: string | undefined;
  id?: string | undefined;
  className?: string | undefined;
  children: (ids: FieldIds) => ReactNode;
}

/**
 * Обёртка поля DESIGN_SYSTEM §5.7: метка сверху обычным регистром, контрол,
 * подсказка и ошибка снизу. Плавающих меток нет. Используется `TextField`,
 * `Select` и любыми контролами форм, которым нужна та же рамка доступности.
 */
export function Field({
  label,
  hideLabel = false,
  hint,
  error,
  id,
  className,
  children,
}: FieldProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const hintId = `${fieldId}-hint`;
  const errorId = `${fieldId}-error`;

  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  return (
    <div className={[styles.field, className].filter(Boolean).join(' ')}>
      <label className={hideLabel ? 'visually-hidden' : styles.label} htmlFor={fieldId}>
        {label}
      </label>

      {children({ fieldId, describedBy, invalid: Boolean(error) })}

      {error ? (
        <p id={errorId} className={styles.error} role="alert">
          <WarningCircleIcon size={16} aria-hidden="true" />
          {error}
        </p>
      ) : hint ? (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
